import React, { useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ModalContext } from "../../contexts/modal/context";

export const ModalRenderer = (): JSX.Element => {
  const { modal, isOpen, closeModal, goToNextStep, goToPreviousStep } =
    useContext(ModalContext);
  const theme = useTheme();

  if (!isOpen || !modal) {
    return <></>;
  }

  const currentStep = modal.steps[modal.currentStepIndex];

  return (
    <Dialog
      open={isOpen}
      onClose={modal.persistent ? undefined : closeModal}
      disableEscapeKeyDown={modal.disableEscapeKeyDown || modal.persistent}
      maxWidth={modal.maxWidth || "sm"}
      fullWidth={modal.fullWidth !== false}
      sx={{
        "& .MuiDialog-paper": {
          position: "relative",
        },
      }}
    >
      {/* Modal Header */}
      <DialogTitle
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {currentStep?.title || "Modal"}
          {!modal.disableStepViewer && modal.steps.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              ({modal.currentStepIndex + 1} of {modal.steps.length})
            </Typography>
          )}
        </Box>
        {!modal.persistent && (
          <IconButton
            onClick={closeModal}
            sx={{
              p: 0,
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* Step Indicator for multi-step modals */}
      {!modal.disableStepViewer && modal.steps.length > 1 && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Stepper activeStep={modal.currentStepIndex} alternativeLabel>
            {modal.steps.map((step, stepIndex) => (
              <Step key={step.id || stepIndex}>
                <StepLabel>{step.title || `Step ${stepIndex + 1}`}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      {/* Modal Content */}
      <DialogContent sx={{ p: 2 }}>{currentStep?.content}</DialogContent>

      {/* Modal Actions */}
      {currentStep?.disableActions ? null : (
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {currentStep?.customActions || (
            <>
              {/* Back Button */}
              {modal.currentStepIndex > 0 &&
                currentStep?.canGoBack !== false && (
                  <Button
                    onClick={goToPreviousStep}
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                  >
                    {currentStep?.backButtonText || "Back"}
                  </Button>
                )}

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Close Button for single step or last step */}
              {(modal.steps.length === 1 ||
                modal.currentStepIndex === modal.steps.length - 1) && (
                <Button onClick={closeModal} variant="outlined">
                  Close
                </Button>
              )}

              {/* Next Button */}
              {modal.currentStepIndex < modal.steps.length - 1 &&
                currentStep?.canGoNext !== false && (
                  <Button
                    onClick={goToNextStep}
                    endIcon={<ArrowForwardIcon />}
                    variant="contained"
                  >
                    {currentStep?.nextButtonText || "Next"}
                  </Button>
                )}
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};
